/**
 * 全局导航协调器
 * 
 * 这个协调器的作用就像交通管制系统，确保多个导航组件不会互相冲突。
 * 它采用"先到先得"的原则，同一时间只允许一个导航系统处于活跃状态。
 * 
 * 主要功能：
 * 1. 注册和管理导航系统
 * 2. 防止资源冲突
 * 3. 统一的清理机制
 * 4. 调试和监控功能
 */

window.ShopifyNavigationCoordinator = (function() {
  'use strict';
  
  /**
   * 私有状态管理
   * 将所有状态封装在闭包中，避免全局污染
   */
  const state = {
    activeSystem: null,           // 当前活跃的导航系统
    registeredSystems: new Map(), // 所有注册的系统
    observers: new Map(),         // Intersection Observer 实例管理
    globalEventListeners: new Map(), // 全局事件监听器管理
    debugMode: false              // 调试模式
  };

  /**
   * 调试日志函数
   * 只在调试模式下输出，避免生产环境的控制台污染
   */
  function debugLog(message, data = null) {
    if (state.debugMode) {
      console.log(`[Navigation Coordinator] ${message}`, data || '');
    }
  }

  /**
   * 系统注册函数
   * 
   * 这就像在交通管制中心登记一架新飞机。每个导航系统都需要
   * 提供自己的"身份证"和"飞行计划"，协调器会检查是否有冲突。
   * 
   * @param {string} systemId - 系统唯一标识符
   * @param {Object} config - 系统配置
   * @returns {boolean} - 注册是否成功
   */
  function registerSystem(systemId, config = {}) {
    // 检查是否已有活跃系统
    if (state.activeSystem && state.activeSystem !== systemId) {
      debugLog(`Registration blocked: ${state.activeSystem} is already active`);
      return false;
    }

    // 注册系统信息
    state.registeredSystems.set(systemId, {
      id: systemId,
      config: config,
      registeredAt: Date.now(),
      observers: [],
      eventListeners: []
    });

    // 设置为活跃系统
    state.activeSystem = systemId;
    
    debugLog(`System registered: ${systemId}`, config);
    return true;
  }

  /**
   * 创建受管理的 Intersection Observer
   * 
   * 这个函数创建的 Observer 会被自动跟踪和管理，确保在系统
   * 卸载时能够正确清理，防止内存泄漏。
   * 
   * @param {string} systemId - 所属系统ID
   * @param {Function} callback - Observer 回调函数
   * @param {Object} options - Observer 配置选项
   * @returns {IntersectionObserver|null} - Observer 实例或 null
   */
  function createManagedObserver(systemId, callback, options = {}) {
    if (!isSystemActive(systemId)) {
      debugLog(`Observer creation blocked for inactive system: ${systemId}`);
      return null;
    }

    // 创建包装后的回调函数，添加系统检查
    const wrappedCallback = function(entries, observer) {
      // 只有当系统仍然活跃时才执行回调
      if (isSystemActive(systemId)) {
        callback(entries, observer);
      }
    };

    const observer = new IntersectionObserver(wrappedCallback, options);
    
    // 注册到系统管理
    const systemData = state.registeredSystems.get(systemId);
    if (systemData) {
      systemData.observers.push(observer);
    }
    
    // 全局管理
    const observerId = `${systemId}_observer_${Date.now()}`;
    state.observers.set(observerId, {
      observer: observer,
      systemId: systemId,
      createdAt: Date.now()
    });

    debugLog(`Observer created for system: ${systemId}`);
    return observer;
  }

  /**
   * 添加受管理的事件监听器
   * 
   * 类似于 Observer，这些事件监听器也会被自动跟踪和清理。
   * 这防止了"僵尸监听器"的问题。
   * 
   * @param {string} systemId - 所属系统ID
   * @param {Element} element - 目标元素
   * @param {string} event - 事件类型
   * @param {Function} handler - 事件处理函数
   * @param {Object} options - 事件选项
   */
  function addManagedEventListener(systemId, element, event, handler, options = {}) {
    if (!isSystemActive(systemId)) {
      debugLog(`Event listener blocked for inactive system: ${systemId}`);
      return;
    }

    // 创建包装后的处理函数
    const wrappedHandler = function(e) {
      if (isSystemActive(systemId)) {
        handler(e);
      }
    };

    element.addEventListener(event, wrappedHandler, options);
    
    // 注册到系统管理
    const systemData = state.registeredSystems.get(systemId);
    if (systemData) {
      systemData.eventListeners.push({
        element: element,
        event: event,
        handler: wrappedHandler,
        options: options
      });
    }

    debugLog(`Event listener added for system: ${systemId} (${event})`);
  }

  /**
   * 检查系统是否处于活跃状态
   * 
   * @param {string} systemId - 系统ID
   * @returns {boolean} - 是否活跃
   */
  function isSystemActive(systemId) {
    return state.activeSystem === systemId;
  }

  /**
   * 创建命名空间化的滚动函数
   * 
   * 这解决了全局函数命名冲突的问题。每个系统都有自己的
   * 滚动函数，但它们都通过协调器来执行，确保不会相互干扰。
   * 
   * @param {string} systemId - 系统ID
   * @param {Object} scrollConfig - 滚动配置
   * @returns {Function} - 专用的滚动函数
   */
  function createScrollFunction(systemId, scrollConfig = {}) {
    return function(targetId) {
      if (!isSystemActive(systemId)) {
        debugLog(`Scroll blocked for inactive system: ${systemId}`);
        return;
      }

      const targetElement = document.querySelector(targetId);
      if (!targetElement) {
        debugLog(`Scroll target not found: ${targetId}`);
        return;
      }

      // 计算动态偏移量
      let offset = scrollConfig.offset || 100;
      
      if (scrollConfig.dynamicOffset) {
        // 重新计算当前的实际偏移量
        const stickyElements = document.querySelectorAll('[style*="position: sticky"], [style*="position: fixed"], .sticky, .fixed');
        let dynamicOffset = 0;
        
        stickyElements.forEach(element => {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 10 && rect.bottom > 0) { // 在顶部的sticky/fixed元素
            dynamicOffset += rect.height;
          }
        });
        
        // 添加安全边距
        offset = Math.max(offset, dynamicOffset + 20);
      }
      
      const behavior = scrollConfig.behavior || 'smooth';
      
      const targetRect = targetElement.getBoundingClientRect();
      const currentScrollPosition = window.pageYOffset;
      const finalPosition = targetRect.top + currentScrollPosition - offset;

      // 确保不会滚动到负数位置
      const scrollTop = Math.max(0, finalPosition);

      window.scrollTo({
        top: scrollTop,
        behavior: behavior
      });

      debugLog(`Scroll executed for system: ${systemId} to ${targetId} (offset: ${offset}px, final: ${scrollTop}px)`);
    };
  }

  /**
   * 系统卸载函数
   * 
   * 这就像飞机离开空域时通知交通管制。系统卸载时会清理
   * 所有相关资源，确保不会留下"垃圾"影响其他系统。
   * 
   * @param {string} systemId - 要卸载的系统ID
   */
  function unregisterSystem(systemId) {
    const systemData = state.registeredSystems.get(systemId);
    if (!systemData) {
      debugLog(`Attempted to unregister unknown system: ${systemId}`);
      return;
    }

    // 清理 Intersection Observers
    systemData.observers.forEach(observer => {
      observer.disconnect();
    });

    // 清理事件监听器
    systemData.eventListeners.forEach(listener => {
      listener.element.removeEventListener(
        listener.event, 
        listener.handler, 
        listener.options
      );
    });

    // 从全局管理中移除
    state.observers.forEach((data, id) => {
      if (data.systemId === systemId) {
        state.observers.delete(id);
      }
    });

    // 移除系统注册
    state.registeredSystems.delete(systemId);
    
    // 如果这是活跃系统，清空活跃状态
    if (state.activeSystem === systemId) {
      state.activeSystem = null;
    }

    debugLog(`System unregistered: ${systemId}`);
  }

  /**
   * 获取系统状态信息（用于调试）
   */
  function getSystemStatus() {
    return {
      activeSystem: state.activeSystem,
      registeredSystems: Array.from(state.registeredSystems.keys()),
      observerCount: state.observers.size,
      debugMode: state.debugMode
    };
  }

  /**
   * 启用调试模式
   */
  function enableDebugMode() {
    state.debugMode = true;
    debugLog('Debug mode enabled');
  }

  /**
   * 紧急清理函数
   * 
   * 在极端情况下，如果检测到系统异常，可以使用这个函数
   * 强制清理所有资源。
   */
  function emergencyCleanup() {
    debugLog('Emergency cleanup initiated');
    
    // 断开所有 Observers
    state.observers.forEach(data => {
      data.observer.disconnect();
    });
    
    // 清空所有状态
    state.observers.clear();
    state.registeredSystems.clear();
    state.activeSystem = null;
    
    debugLog('Emergency cleanup completed');
  }

  // 页面卸载时自动清理
  window.addEventListener('beforeunload', function() {
    emergencyCleanup();
  });

  /**
   * 公开 API
   * 
   * 这些是外部系统可以调用的接口，就像交通管制塔的
   * 通信频道，其他系统通过这些接口与协调器通信。
   */
  return {
    // 核心功能
    register: registerSystem,
    unregister: unregisterSystem,
    isActive: isSystemActive,
    
    // 资源管理
    createObserver: createManagedObserver,
    addEventListener: addManagedEventListener,
    createScrollFunction: createScrollFunction,
    
    // 调试和监控
    getStatus: getSystemStatus,
    enableDebug: enableDebugMode,
    emergency: emergencyCleanup
  };
})();